import axios, { AxiosRequestConfig } from 'axios';
import { useAppStore } from '../stores/useAppStore';

// 定义标准响应结构
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data: T;
  [key: string]: any;
}

console.log(
  '=== 后端 API 客户端已加载 v4.0 (Fastify + Prisma + Redis) ===',
  new Date().toISOString(),
  '?v=' + Math.random(),
);

// 后端API配置 - 版本 4.0 (Fastify + Prisma + Redis)
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://local-api.fungitoys.com';
const API_VERSION = '/api/v2'; // 使用新的 v2 API

console.log('API配置:', {
  BACKEND_BASE_URL,
  API_VERSION,
  fullURL: BACKEND_BASE_URL + API_VERSION,
  envVar: process.env.REACT_APP_BACKEND_URL,
  isFromEnv: !!process.env.REACT_APP_BACKEND_URL,
});

// 验证配置
if (!BACKEND_BASE_URL) {
  console.error('❌ BACKEND_BASE_URL 未配置！请设置 REACT_APP_BACKEND_URL 环境变量');
} else {
  console.log('✅ API配置正常:', BACKEND_BASE_URL);
}

// 创建axios实例
const axiosInstance = axios.create({
  baseURL: BACKEND_BASE_URL + API_VERSION,
  timeout: 300000, // 300秒超时（5分钟，支持AI分析等长时间请求）
});

// 请求拦截器
axiosInstance.interceptors.request.use(
  (config) => {
    // 添加认证token（如果需要）
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // 添加缓存控制头来破坏Cloudflare缓存（简化版本避免CORS问题）
    config.headers['Cache-Control'] = 'no-cache';

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 响应拦截器
axiosInstance.interceptors.response.use(
  (response) => {
    // 直接返回完整的响应数据，包含 success 和 data 字段
    return response.data;
  },
  (error) => {
    console.error('Backend API Error:', error);

    // 处理401未授权错误
    if (error.response?.status === 401) {
      console.log('🔐 收到401错误，清空本地缓存并重新授权');

      // 设置401错误标志，避免重复验证
      sessionStorage.setItem('auth_401_error', 'true');

      // 清空本地缓存
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      localStorage.removeItem('wallet');

      // 清空应用状态
      const { clearAllState } = useAppStore.getState();
      clearAllState();

      // 触发重新授权流程
      if (window.Telegram?.WebApp) {
        console.log('🔄 触发Telegram重新授权');
        // 可以在这里添加重新授权的逻辑
        // 比如显示授权页面或重新获取用户信息
      }
    }

    // 处理403权限不足错误
    if (error.response?.status === 403) {
      const permissionMessage =
        error.response?.data?.message || error.response?.data?.error?.message || '权限不足：请联系管理员开通权限';

      const permissionError = new Error(permissionMessage);
      (permissionError as any).response = error.response;
      (permissionError as any).code = 403;
      return Promise.reject(permissionError);
    }

    // 处理404等错误，确保error.response.data被保留，以便后续提取message
    // 后端可能返回 {success: false, message: "..."}，需要保留这个结构
    if (error.response?.data) {
      // 如果后端返回了message，创建一个包含message的错误
      const errorMessage = error.response.data.message || error.response.data.error;
      if (errorMessage) {
        console.log('apiClient错误拦截器: 提取错误消息:', errorMessage);
        const customError = new Error(errorMessage);
        (customError as any).response = error.response;
        (customError as any).code = error.response.status;
        return Promise.reject(customError);
      }
    }

    console.log('apiClient错误拦截器: 没有找到message，返回原始错误');
    return Promise.reject(error);
  },
);

// 定义强类型的ApiClient接口
interface TypedApiClient {
  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  head<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  options<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>>;
  defaults: any;
  interceptors: any;
}

// 导出强类型的apiClient
const apiClient = axiosInstance as unknown as TypedApiClient;

export default apiClient;
export { apiClient };
